import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { API_BASE, api } from '@/api/client';
import type { Hospital } from '@/api/types';

/** The signed-in user's hospital (name, address, logo version). */
export function useHospital() {
  return useQuery({ queryKey: ['hospital'], queryFn: () => api.get<Hospital>('/api/v1/hospital'), staleTime: 5 * 60_000 });
}

/** Public URL of a hospital's photo; the version changes whenever a new photo is uploaded. */
export function hospitalPhotoUrl(hospitalId: string, version?: string | null) {
  return `${API_BASE}/api/v1/public/hospitals/${hospitalId}/photo?v=${encodeURIComponent(version ?? '')}`;
}

/**
 * The hospital's logo: its uploaded photo, or the default HMS mark when there is none
 * (or the image cannot be loaded).
 */
export function HospitalLogo({
  hospitalId,
  version,
  hasPhoto,
  alt = '',
  className = '',
  size,
  fallback,
}: {
  hospitalId?: string | null;
  version?: string | null;
  hasPhoto?: boolean;
  alt?: string;
  className?: string;
  size?: number;
  fallback?: ReactNode;
}) {
  const [failed, setFailed] = useState<string | null>(null);
  const url = hospitalId && hasPhoto ? hospitalPhotoUrl(hospitalId, version) : null;
  const style = size ? { width: size, height: size } : undefined;
  if (!url || failed === url)
    return (
      <div className={`brand-logo ${className}`} style={style}>
        {fallback ?? <Plus size={size ? Math.round(size * 0.58) : 20} strokeWidth={3} />}
      </div>
    );
  return (
    <div className={`brand-logo has-photo ${className}`} style={style}>
      <img src={url} alt={alt} onError={() => setFailed(url)} />
    </div>
  );
}
